import { Heart } from 'lucide-react';

export const PAGE_BUILDER_ORDER = ['home', 'our_story', 'photos', 'wedding_party', 'qa', 'travel', 'rsvp'];

const PAGE_SOURCE_ALIASES = {
  home: ['title'],
  our_story: ['story'],
  photos: ['photo_gallery', 'photo'],
  wedding_party: [],
  qa: [],
  travel: ['travel_page', 'text_block', 'travel', 'schedule'],
  rsvp: ['rsvp_page', 'rsvp'],
};

const PAGE_TITLES = {
  home: 'Wedding Website',
  our_story: 'Our Story',
  photos: 'Photos',
  wedding_party: 'Wedding Party',
  qa: 'Q&A',
  travel: 'Wedding Day',
  rsvp: 'RSVP',
};

const THEME_MAP = {
  'stone-ivory': { pageBg: '#f8f6f2', pageText: '#1f1b16', surface: '#ffffff', border: '#ddd5c9', accent: '#5c0120', accentText: '#ffffff' },
  'blush-champagne': { pageBg: '#fcf5f6', pageText: '#3c2430', surface: '#fffdf9', border: '#e8d4da', accent: '#8f2f56', accentText: '#ffffff' },
  'coastal-pearl': { pageBg: '#f2f7fb', pageText: '#1c3347', surface: '#ffffff', border: '#c7d9e6', accent: '#2d5f86', accentText: '#ffffff' },
  'sage-linen': { pageBg: '#f1f6f1', pageText: '#27372a', surface: '#ffffff', border: '#cedacb', accent: '#3f6a4b', accentText: '#ffffff' },
};

const HEADING_FONT_MAP = {
  'timeless-seriffed': "'Cormorant Garamond', serif",
  'editorial-grand': "'Playfair Display', serif",
  'refined-contemporary': "'Playfair Display', serif",
  'modern-editorial': "'Manrope', sans-serif",
};

const BODY_FONT_MAP = {
  'timeless-seriffed': "'Manrope', sans-serif",
  'editorial-grand': "'Manrope', sans-serif",
  'refined-contemporary': "'Inter', sans-serif",
  'modern-editorial': "'Manrope', sans-serif",
};

const SPACING_CLASS_MAP = {
  compact: 'space-y-3',
  balanced: 'space-y-4',
  airy: 'space-y-6',
  'airy-plus': 'space-y-8',
};

const RSVP_GROUP_OPTIONS = ['Family - Bride', 'Family - Groom', 'Friends', 'Work Colleagues', 'Other'];

const VALID_LAYOUTS = new Set(['full-width', 'image-left', 'image-right']);

const formatDisplayDate = (dateValue) => {
  if (!dateValue) return '';
  const parsed = new Date(dateValue);
  if (Number.isNaN(parsed.getTime())) return dateValue;
  return parsed.toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' });
};

const resolvePageSource = (sectionConfig, pageKey) => {
  if (!sectionConfig || typeof sectionConfig !== 'object') return null;
  const keys = [pageKey, ...(PAGE_SOURCE_ALIASES[pageKey] || [])];
  for (const key of keys) {
    const value = sectionConfig[key];
    if (value && typeof value === 'object') return value;
  }
  return null;
};

const normalizeBlocks = (blocks) => {
  if (!Array.isArray(blocks)) return [];
  return blocks
    .filter((block) => block && typeof block === 'object')
    .map((block, index) => {
      const layout = VALID_LAYOUTS.has(block.layout) ? block.layout : 'full-width';
      return {
        id: block.id || `block-${index}`,
        type: block.type || 'text',
        title: block.title || '',
        text: block.text || '',
        image_url: block.image_url || '',
        images: Array.isArray(block.images) ? block.images.filter(Boolean) : [],
        image_orientation: block.image_orientation === 'portrait' ? 'portrait' : 'landscape',
        layout,
      };
    });
};

const normalizeScheduleEntries = (entries) => {
  if (!Array.isArray(entries)) return [];
  return entries
    .filter((entry) => entry && typeof entry === 'object')
    .map((entry, index) => ({
      id: entry.id || `schedule-${index}`,
      time: entry.time || '',
      description: entry.description || '',
      location: entry.location || '',
    }))
    .filter((entry) => entry.time || entry.description || entry.location);
};

const toSortableTime = (value) => {
  const raw = String(value || '').trim().toLowerCase();
  if (!raw) return null;

  const twentyFourMatch = raw.match(/^(\d{1,2}):(\d{2})$/);
  if (twentyFourMatch) {
    const hours = Number(twentyFourMatch[1]);
    const minutes = Number(twentyFourMatch[2]);
    if (hours >= 0 && hours <= 23 && minutes >= 0 && minutes <= 59) {
      return hours * 60 + minutes;
    }
  }

  const twelveHourMatch = raw.match(/^(\d{1,2})(?::(\d{2}))?\s*(am|pm)$/);
  if (twelveHourMatch) {
    let hours = Number(twelveHourMatch[1]);
    const minutes = Number(twelveHourMatch[2] || '0');
    const suffix = twelveHourMatch[3];
    if (hours >= 1 && hours <= 12 && minutes >= 0 && minutes <= 59) {
      if (suffix === 'pm' && hours !== 12) hours += 12;
      if (suffix === 'am' && hours === 12) hours = 0;
      return hours * 60 + minutes;
    }
  }

  return null;
};

const sortScheduleEntries = (entries) => {
  return [...entries]
    .map((entry, index) => ({
      ...entry,
      _index: index,
      _timeValue: toSortableTime(entry.time),
    }))
    .sort((a, b) => {
      const aHasTime = a._timeValue !== null;
      const bHasTime = b._timeValue !== null;
      if (aHasTime && bHasTime) return a._timeValue - b._timeValue;
      if (aHasTime && !bHasTime) return -1;
      if (!aHasTime && bHasTime) return 1;
      return a._index - b._index;
    })
    .map(({ _index, _timeValue, ...entry }) => entry);
};

const formatScheduleTimeDisplay = (value) => {
  const raw = String(value || '').trim();
  if (!raw) return 'Time to be confirmed';

  const twentyFourMatch = raw.match(/^(\d{1,2}):(\d{2})$/);
  if (twentyFourMatch) {
    const hours = Number(twentyFourMatch[1]);
    const minutes = Number(twentyFourMatch[2]);
    if (hours >= 0 && hours <= 23 && minutes >= 0 && minutes <= 59) {
      const period = hours >= 12 ? 'PM' : 'AM';
      const normalizedHour = hours % 12 === 0 ? 12 : hours % 12;
      return `${normalizedHour}:${String(minutes).padStart(2, '0')} ${period}`;
    }
  }

  return raw;
};

const normalizePage = (pageKey, sourcePage) => {
  const page = sourcePage && typeof sourcePage === 'object' ? sourcePage : {};
  return {
    visible: typeof page.visible === 'boolean' ? page.visible : (pageKey === 'rsvp' ? false : true),
    title: page.title || PAGE_TITLES[pageKey],
    text: page.text || '',
    image_url: page.image_url || '',
    image_orientation: page.image_orientation === 'portrait' ? 'portrait' : 'landscape',
    layout: VALID_LAYOUTS.has(page.layout) ? page.layout : 'full-width',
    schedule_entries: normalizeScheduleEntries(page.schedule_entries),
    blocks: normalizeBlocks(page.blocks),
  };
};

export const hasRenderablePageBuilderConfig = (sectionConfig) => {
  if (!sectionConfig || typeof sectionConfig !== 'object') return false;
  return PAGE_BUILDER_ORDER.some((pageKey) => {
    const page = resolvePageSource(sectionConfig, pageKey);
    if (!page || typeof page !== 'object') return false;
    return Boolean(
      page.title
      || page.text
      || page.image_url
      || (Array.isArray(page.schedule_entries) && page.schedule_entries.length > 0)
      || (Array.isArray(page.blocks) && page.blocks.length > 0)
      || typeof page.visible === 'boolean'
    );
  });
};

export const buildWeddingV2RenderModel = ({
  sectionConfig,
  designSettings,
  content,
  rsvpEnabled = true,
} = {}) => {
  const pages = {};
  PAGE_BUILDER_ORDER.forEach((pageKey) => {
    pages[pageKey] = normalizePage(pageKey, resolvePageSource(sectionConfig, pageKey));
  });

  if (pages.travel.schedule_entries.length === 0 && Array.isArray(content?.schedule)) {
    pages.travel.schedule_entries = normalizeScheduleEntries(
      content.schedule.map((item, index) => ({
        id: item?.id || `content-schedule-${index}`,
        time: item?.time || '',
        description: item?.title || '',
        location: item?.notes || '',
      })),
    );
  }

  return {
    pages,
    designSettings: designSettings || {},
    content: {
      couple_names: content?.couple_names || '',
      date: content?.date || '',
      location: content?.location || '',
      photos: Array.isArray(content?.photos) ? content.photos.filter(Boolean) : [],
    },
    rsvpEnabled: rsvpEnabled !== false,
  };
};

const getTextAlign = (value) => {
  const normalized = String(value || '').toLowerCase();
  if (normalized === 'left' || normalized === 'right' || normalized === 'center') return normalized;
  return 'center';
};

const renderMediaLayout = ({ url, orientation, layout, index, altText, borderColor, testIdPrefix, testIdSuffix }) => {
  if (!url) return null;

  if (orientation === 'portrait') {
    const imageOnLeft = layout === 'image-left' || (layout !== 'image-right' && index % 2 === 0);
    return (
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4" data-testid={`${testIdPrefix}-${testIdSuffix}-portrait-wrap`}>
        {imageOnLeft && (
          <div className="overflow-hidden border" style={{ borderColor }}>
            <img src={url} alt={altText} className="w-full h-full aspect-[4/5] object-cover object-center" data-testid={`${testIdPrefix}-${testIdSuffix}-portrait-image-left`} />
          </div>
        )}
        {!imageOnLeft && <div className="hidden md:block" />}
        {!imageOnLeft && (
          <div className="overflow-hidden border" style={{ borderColor }}>
            <img src={url} alt={altText} className="w-full h-full aspect-[4/5] object-cover object-center" data-testid={`${testIdPrefix}-${testIdSuffix}-portrait-image-right`} />
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="w-full overflow-hidden border mt-4" style={{ borderColor }} data-testid={`${testIdPrefix}-${testIdSuffix}-landscape-wrap`}>
      <img src={url} alt={altText} className="w-full h-full aspect-[16/9] object-cover object-center" data-testid={`${testIdPrefix}-${testIdSuffix}-landscape-image`} />
    </div>
  );
};

export const WeddingWebsiteV2Renderer = ({
  renderModel,
  renderRsvpSection,
  testIdPrefix = 'wedding-public',
}) => {
  const model = renderModel || buildWeddingV2RenderModel();
  const theme = THEME_MAP[model.designSettings?.color_theme] || THEME_MAP['stone-ivory'];
  const headingFont = HEADING_FONT_MAP[model.designSettings?.typography_style] || "'Playfair Display', serif";
  const bodyFont = BODY_FONT_MAP[model.designSettings?.typography_style] || "'Manrope', sans-serif";
  const spacingClass = SPACING_CLASS_MAP[model.designSettings?.spacing_style] || 'space-y-6';
  const textAlign = getTextAlign(model.designSettings?.text_alignment);
  const headingClass = model.designSettings?.uppercase_headings ? 'uppercase tracking-[0.12em]' : '';

  const pages = model.pages || {};
  const homePage = pages.home || {};
  const coupleNames = model.content?.couple_names || 'Wedding Couple';
  const fallbackPhotos = Array.isArray(model.content?.photos) ? model.content.photos : [];
  const descriptiveAlt = `${coupleNames} wedding Gibraltar`;

  const renderRsvp = () => {
    if (typeof renderRsvpSection === 'function') {
      return renderRsvpSection({ theme });
    }

    return (
      <section className="border p-6 space-y-4" style={{ borderColor: theme.border, backgroundColor: theme.surface }} data-testid={`${testIdPrefix}-rsvp-preview-form`}>
        <h2 className={`text-2xl font-semibold mb-1 ${headingClass}`} style={{ fontFamily: headingFont, color: theme.accent }}>RSVP</h2>
        <p className="text-sm leading-relaxed" data-testid={`${testIdPrefix}-rsvp-preview-caption`}>Preview of your live RSVP form fields.</p>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-3" data-testid={`${testIdPrefix}-rsvp-preview-fields`}>
          <div>
            <p className="text-xs font-semibold mb-1">Name *</p>
            <div className="h-10 rounded-md border" style={{ borderColor: theme.border, backgroundColor: '#ffffff' }} data-testid={`${testIdPrefix}-rsvp-preview-name`} />
          </div>
          <div>
            <p className="text-xs font-semibold mb-1">Email</p>
            <div className="h-10 rounded-md border" style={{ borderColor: theme.border, backgroundColor: '#ffffff' }} data-testid={`${testIdPrefix}-rsvp-preview-email`} />
          </div>
          <div>
            <p className="text-xs font-semibold mb-1">Phone</p>
            <div className="h-10 rounded-md border" style={{ borderColor: theme.border, backgroundColor: '#ffffff' }} data-testid={`${testIdPrefix}-rsvp-preview-phone`} />
          </div>
          <div>
            <p className="text-xs font-semibold mb-1">Group</p>
            <div className="h-10 rounded-md border px-3 flex items-center text-xs" style={{ borderColor: theme.border, backgroundColor: '#ffffff' }} data-testid={`${testIdPrefix}-rsvp-preview-group`}>
              {RSVP_GROUP_OPTIONS.join(' • ')}
            </div>
          </div>
          <div>
            <p className="text-xs font-semibold mb-1">Dietary Requirements</p>
            <div className="h-10 rounded-md border" style={{ borderColor: theme.border, backgroundColor: '#ffffff' }} data-testid={`${testIdPrefix}-rsvp-preview-dietary`} />
          </div>
          <div>
            <p className="text-xs font-semibold mb-1">Additional Guests Count</p>
            <div className="h-10 rounded-md border" style={{ borderColor: theme.border, backgroundColor: '#ffffff' }} data-testid={`${testIdPrefix}-rsvp-preview-additional-count`} />
          </div>
        </div>

        <button
          type="button"
          className="w-full rounded-md py-2.5 text-sm font-semibold"
          style={{ backgroundColor: theme.accent, color: theme.accentText }}
          data-testid={`${testIdPrefix}-rsvp-preview-submit-button`}
        >
          Submit RSVP
        </button>
      </section>
    );
  };

  return (
    <div className="min-h-screen" style={{ backgroundColor: theme.pageBg, color: theme.pageText, fontFamily: bodyFont }} data-testid={`${testIdPrefix}-page-v2`}>
      <header
        className={`px-6 py-14 border-b ${spacingClass}`}
        style={{
          borderColor: theme.border,
          backgroundImage: homePage?.image_url ? `linear-gradient(rgba(0,0,0,0.35), rgba(0,0,0,0.35)), url('${homePage.image_url}')` : 'none',
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          color: homePage?.image_url ? '#ffffff' : theme.pageText,
          textAlign,
        }}
        data-testid={`${testIdPrefix}-v2-hero`}
      >
        <p className="text-xs uppercase tracking-[0.2em] mb-2" data-testid={`${testIdPrefix}-hero-title`}>{homePage?.title || PAGE_TITLES.home}</p>
        <h1 className={`text-4xl sm:text-5xl lg:text-6xl font-semibold ${headingClass}`} style={{ fontFamily: headingFont }} data-testid={`${testIdPrefix}-couple-names`}>{coupleNames}</h1>
        {(model.content?.date || model.content?.location) && (
          <p className="mt-3 text-sm sm:text-base" data-testid={`${testIdPrefix}-date-location`}>{formatDisplayDate(model.content?.date) || 'Date to be confirmed'} {model.content?.location ? `· ${model.content.location}` : ''}</p>
        )}
        {homePage?.text && <p className="mt-4 max-w-2xl mx-auto whitespace-pre-line" data-testid={`${testIdPrefix}-hero-text`}>{homePage.text}</p>}
      </header>

      <main className={`max-w-4xl mx-auto px-6 py-8 ${spacingClass}`} style={{ textAlign }} data-testid={`${testIdPrefix}-v2-sections-wrap`}>
        {PAGE_BUILDER_ORDER.filter((key) => key !== 'home').map((pageKey, pageIndex) => {
          const page = pages[pageKey];
          if (!page || page.visible === false) return null;

          const pageBlocks = Array.isArray(page.blocks) ? page.blocks : [];
          const sortedScheduleEntries = sortScheduleEntries(page.schedule_entries || []);
          const galleryFallback = pageKey === 'photos' ? fallbackPhotos : [];

          if (pageKey === 'rsvp') {
            return (
              <article key={`${testIdPrefix}-page-${pageKey}`} className="border p-6" style={{ borderColor: theme.border, backgroundColor: theme.surface }} data-testid={`${testIdPrefix}-page-${pageKey}`}>
                {model.rsvpEnabled !== false && renderRsvp()}
              </article>
            );
          }

          return (
            <article key={`${testIdPrefix}-page-${pageKey}`} className="border p-6" style={{ borderColor: theme.border, backgroundColor: theme.surface }} data-testid={`${testIdPrefix}-page-${pageKey}`}>
              <h2 className={`text-2xl font-semibold mb-3 ${headingClass}`} style={{ fontFamily: headingFont, color: theme.accent }} data-testid={`${testIdPrefix}-page-heading-${pageKey}`}>{page.title || PAGE_TITLES[pageKey]}</h2>
              {page.text && <p className="whitespace-pre-line leading-relaxed" data-testid={`${testIdPrefix}-page-text-${pageKey}`}>{page.text}</p>}

              {pageKey === 'travel' && sortedScheduleEntries.length > 0 && (
                <div className="mt-4 space-y-3" data-testid={`${testIdPrefix}-page-schedule-list`}>
                  <p className="text-sm font-semibold uppercase tracking-[0.12em]" style={{ color: theme.accent }} data-testid={`${testIdPrefix}-page-schedule-title`}>
                    Schedule
                  </p>
                  {sortedScheduleEntries.map((entry, entryIndex) => (
                    <div key={entry.id || `${pageKey}-schedule-${entryIndex}`} className="rounded-md border p-3" style={{ borderColor: theme.border }} data-testid={`${testIdPrefix}-page-schedule-row-${entryIndex}`}>
                      <p className="text-sm font-semibold" data-testid={`${testIdPrefix}-page-schedule-time-${entryIndex}`}>{formatScheduleTimeDisplay(entry.time)}</p>
                      {entry.description && <p className="mt-1 text-sm" data-testid={`${testIdPrefix}-page-schedule-description-${entryIndex}`}>{entry.description}</p>}
                      {entry.location && <p className="mt-1 text-xs opacity-80" data-testid={`${testIdPrefix}-page-schedule-location-${entryIndex}`}>{entry.location}</p>}
                    </div>
                  ))}
                </div>
              )}

              {renderMediaLayout({
                url: page.image_url,
                orientation: page.image_orientation,
                layout: page.layout,
                index: pageIndex + 1,
                altText: descriptiveAlt,
                borderColor: theme.border,
                testIdPrefix,
                testIdSuffix: `${pageKey}-page-image`,
              })}

              {pageBlocks.map((block, blockIndex) => (
                <div key={block.id || `${pageKey}-block-${blockIndex}`} className="mt-4" data-testid={`${testIdPrefix}-${pageKey}-block-${blockIndex}`}>
                  {block.type === 'heading' && block.title && <h3 className="text-xl font-semibold" style={{ fontFamily: headingFont, color: theme.accent }} data-testid={`${testIdPrefix}-${pageKey}-block-heading-${blockIndex}`}>{block.title}</h3>}
                  {block.type === 'text' && block.text && <p className="whitespace-pre-line leading-relaxed" data-testid={`${testIdPrefix}-${pageKey}-block-text-${blockIndex}`}>{block.text}</p>}
                  {block.type === 'image' && block.image_url && renderMediaLayout({
                    url: block.image_url,
                    orientation: block.image_orientation,
                    layout: block.layout,
                    index: blockIndex,
                    altText: descriptiveAlt,
                    borderColor: theme.border,
                    testIdPrefix,
                    testIdSuffix: `${pageKey}-block-image-${blockIndex}`,
                  })}
                  {block.type === 'gallery' && Array.isArray(block.images) && block.images.length > 0 && (
                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-2" data-testid={`${testIdPrefix}-${pageKey}-block-gallery-${blockIndex}`}>
                      {block.images.map((photo, photoIndex) => (
                        <img key={`${photo}-${photoIndex}`} src={photo} alt={descriptiveAlt} className="w-full aspect-square object-cover" data-testid={`${testIdPrefix}-${pageKey}-block-gallery-image-${blockIndex}-${photoIndex}`} />
                      ))}
                    </div>
                  )}
                </div>
              ))}

              {pageKey === 'photos' && pageBlocks.length === 0 && galleryFallback.length > 0 && (
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 mt-4" data-testid={`${testIdPrefix}-page-photos-fallback-grid`}>
                  {galleryFallback.map((photo, photoIndex) => (
                    <img key={`${photo}-${photoIndex}`} src={photo} alt={descriptiveAlt} className="w-full aspect-square object-cover" data-testid={`${testIdPrefix}-page-photos-fallback-image-${photoIndex}`} />
                  ))}
                </div>
              )}
            </article>
          );
        })}
      </main>

      <footer className="px-6 py-10 text-center text-sm" data-testid={`${testIdPrefix}-footer`}>
        <p className="flex items-center justify-center gap-2"><Heart className="h-4 w-4" />Powered by Gibraltar Wedding Expo</p>
      </footer>
    </div>
  );
};
